import ResponseError from '@/errors/responseError';

class Model {
	baseUrl = '/api';

	constructor(resource) {
		this.resource = resource;
	}

	get basePath() {
		return `${this.baseUrl}${this.resource}`;
	}

	#addQueryParams(url, params) {
		if (!params) {
			return;
		}

		Object.entries(params).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				value.forEach(val => {
					url.searchParams.append(`${key}[]`, val);
				});
			} else {
				url.searchParams.append(key, value);
			}
		});
	}

	async call({
		completeResponse = false,
		url: urlValue,
		body: bodyValue,
		headers,
		params,
		unauthorizedForceLogout = true,
		...other
	}) {
		try {
			let body = bodyValue;
			let baseHeaders = {};
			const url = new URL(urlValue, window.location.origin);

			this.#addQueryParams(url, params);

			if (
				!(
					bodyValue instanceof FormData ||
					bodyValue instanceof File ||
					ArrayBuffer.isView(bodyValue) ||
					bodyValue instanceof ArrayBuffer ||
					bodyValue instanceof Blob
				)
			) {
				body = JSON.stringify(bodyValue);
				baseHeaders = { 'Content-Type': 'application/json' };
			}

			const response = await fetch(url, {
				...other,
				headers: {
					...baseHeaders,
					...headers
				},
				body
			});

			if (response.status === 204) {
				return true;
			}

			if (!response.ok) {
				throw response;
			}

			if (completeResponse) {
				return response;
			}

			const responseData = await response.json().catch(() => ({}));

			return responseData;
		} catch (error) {
			if (error.status === 401 && unauthorizedForceLogout) {
				window.dispatchEvent(new Event('logout'));
			}

			let data;

			if (
				error instanceof Response &&
				(error.headers.get('content-type') || '').includes(
					'application/json'
				)
			) {
				data = await error.json().catch(() => ({}));
			}

			if (
				error.status === 403 &&
				data.errorCode === 'user_banned' &&
				unauthorizedForceLogout
			) {
				window.dispatchEvent(new Event('logout'));
			}

			if (data) {
				throw new ResponseError(error, {
					data,
					method: other.method || 'GET'
				});
			}

			throw error;
		}
	}

	get(path, config = {}) {
		return this.call({ ...config, url: `${this.basePath}${path}` });
	}

	post(path, config = {}) {
		return this.call({
			...config,
			url: `${this.basePath}${path}`,
			method: 'POST'
		});
	}

	put(path, config = {}) {
		return this.call({
			...config,
			url: `${this.basePath}${path}`,
			method: 'PUT'
		});
	}

	patch(path, config = {}) {
		return this.call({
			...config,
			url: `${this.basePath}${path}`,
			method: 'PATCH'
		});
	}

	delete(path, config = {}) {
		return this.call({
			...config,
			url: `${this.basePath}${path}`,
			method: 'DELETE'
		});
	}

	buildUrl(path, params) {
		const url = new URL(`${this.basePath}${path}`, window.location.origin);
		this.#addQueryParams(url, params);

		return url.toString();
	}
}

export default Model;
